import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { setNotifications, incrementNotificationCount } from '../redux/notificationsSlice';
import { 
  Menu, Bell, LogOut, Search, Settings, Globe, User, Key, Info,
  HardDrive, Wrench, ShoppingCart, BarChart, ShieldAlert, ChevronRight, X, ChevronDown, Type, Plus, Minus,
  Cpu, Server, Database, Monitor, CheckCircle2 // 🎯 أيقونات المودال الجديد
} from 'lucide-react';
import api from './axiosConfig';
import PersonalInformationModal  from './PersonalInformationModal';
import ChangePasswordModal from './ChangePasswordModal';

import { useFontSize } from './FontSizeContext';
import { useAuth } from './AuthContext';
import CloudConfig from './CloudConfig';

// =========================================================================
// 🎯 مكون المودال السحري لمعلومات النظام (System Info Modal)
// =========================================================================
const SystemInfoModal = ({ isOpen, onClose, t, isRTL }) => {
  if (!isOpen) return null;

  const SystemInfoCard = ({ icon, title, value, status, statusColor }) => (
    <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-[0_2px_10px_rgba(0,0,0,0.02)] flex items-center gap-3 relative overflow-hidden group hover:border-[#00a1df] hover:shadow-md transition-all duration-300 cursor-default">
      <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-[#005495] group-hover:bg-blue-50 group-hover:scale-110 transition-all duration-300">
        {icon}
      </div>
      <div className={`flex flex-col z-10 ${isRTL ? 'text-right' : 'text-left'}`}>
        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">{title}</span>
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold text-slate-700 font-mono">{value}</span>
          {status && (
            <span className="flex items-center gap-1 bg-slate-50 px-1.5 py-0.5 rounded-md border border-slate-100">
              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${statusColor}`}></span>
              <span className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">{status}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-300" onClick={onClose}>
      <div 
        className="bg-[#f8f9fa] rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden relative flex flex-col outline-none transform transition-all" 
        onClick={e => e.stopPropagation()} // منع الإغلاق عند الضغط داخل المودال
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header - Gradient with Tech Pattern */}
        <div className="bg-gradient-to-br from-[#0a192f] via-[#005495] to-[#00a1df] p-8 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 opacity-10 rotate-12 scale-150"><Cpu size={200} /></div>
          <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-[#f8f9fa] to-transparent opacity-20"></div>
          
          <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors bg-black/20 hover:bg-black/40 p-2 rounded-full backdrop-blur-md">
            <X size={16} />
          </button>
          
          <div className="relative z-10 flex items-center gap-4">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl flex items-center justify-center shadow-lg">
              <img src="/assets/teknova.png" alt="Logo" className="w-10 h-auto object-contain brightness-0 invert" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white tracking-widest uppercase mb-1 drop-shadow-md">TEKNORA EAM</h2>
              <p className="text-blue-200 text-[10px] font-bold tracking-[0.3em] uppercase flex items-center gap-2">
                Enterprise Asset Management
                <span className="bg-blue-500/30 border border-blue-400/50 text-white px-2 py-0.5 rounded-full text-[8px] tracking-widest">PRO</span>
              </p>
            </div>
          </div>
        </div>

        {/* Body - Grid of System Specs */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <SystemInfoCard icon={<Monitor size={20} />} title={t('sys_info.frontend', 'Frontend Client')} value="v2.4.0-stable" />
            <SystemInfoCard icon={<Server size={20} />} title={t('sys_info.backend', 'Backend API')} value="v2.1.8-fastapi" />
            <SystemInfoCard icon={<Database size={20} />} title={t('sys_info.database', 'Database Engine')} value="PostgreSQL 15" status="Connected" statusColor="bg-green-500" />
            <SystemInfoCard icon={<Globe size={20} />} title={t('sys_info.environment', 'Environment')} value="Production Cloud" status="Live" statusColor="bg-green-500" />
          </div>

          <div className="bg-white border border-blue-100 rounded-xl p-4 flex items-start gap-4 shadow-sm relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-1 h-full bg-blue-500"></div>
            <div className="bg-blue-50 p-2 rounded-lg shrink-0">
              <ShieldAlert className="text-[#005495]" size={20} />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-black text-[#005495] uppercase tracking-widest">{t('sys_info.license', 'License Information')}</span>
              <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                {t('sys_info.license_text', 'This product is licensed to your organization. Unauthorized reproduction, distribution, or reverse engineering of this software is strictly prohibited under intellectual property laws.')}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white p-4 px-6 flex justify-between items-center border-t border-slate-200">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} className="text-green-500" />
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">System Operational</span>
          </div>
          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">© {new Date().getFullYear()} Teknora Inc.</span>
        </div>
      </div>
    </div>
  );
};
// =========================================================================

const Header = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const location = useLocation(); 
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPersonalModalOpen, setIsPersonalModalOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isSystemInfoOpen, setIsSystemInfoOpen] = useState(false); // 🎯 State المودال الجديد
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false); 
  const [userData, setUserData] = useState({ name: 'Guest', role: 'User', initial: 'G' });
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  
  const langMenuRef = useRef(null); 
  const menuRef = useRef(null);
  const userMenuRef = useRef(null); 

  const [isNotifMenuOpen, setIsNotifMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifMenuRef = useRef(null);

  // 1️⃣ ربط الـ WebSocket واستقبال الإشعارات لحظياً
  useEffect(() => {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return;
      
      const { username } = JSON.parse(storedUser);
      const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
      const currentHost = window.location.host;
      const wsUrl = `${wsProtocol}${currentHost}/ws/${username}`;
      const ws = new WebSocket(wsUrl);

      ws.onmessage = (event) => {
          const data = JSON.parse(event.target ? event.target.data : event.data);
          setNotifications(prev => [data, ...prev]);
          setUnreadCount(prev => prev + 1);
      };

      return () => ws.close();
  }, []);

  // 2️⃣ جلب تاريخ الإشعارات
  useEffect(() => {
      const fetchNotifications = async () => {
          try {
              const res = await api.get('/notifications/unread');
              setNotifications(res.data);
              setUnreadCount(res.data.length);
          } catch (err) {
              console.error("Error fetching notifications", err);
          }
      };
      fetchNotifications();
  }, []);

  const handleNotifClick = async (notif) => {
      try {
          await api.post(`/notifications/mark-read/${notif.id}`);
          setNotifications(prev => prev.filter(n => n.id !== notif.id));
          setUnreadCount(prev => Math.max(0, prev - 1));
          setIsNotifMenuOpen(false);
          navigate(notif.link); 
      } catch (err) {
          navigate(notif.link);
      }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token && location.pathname !== '/login') {
      navigate('/login');
    } else if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUserData({
          name: parsedUser.display_name || parsedUser.username || 'Admin',
          role: parsedUser.role.name || 'Administrator',
          initial: (parsedUser.display_name || parsedUser.username || 'A').charAt(0).toUpperCase()
        });
      } catch (e) {
        console.error("Error parsing user data");
      }
    }
  }, [navigate, location.pathname]);
  
  const FontControls = () => {
    const { fontSize, changeFontSize } = useFontSize();
  
    return (
      <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
        <button onClick={() => changeFontSize(-1)} className="hover:text-orange-400 transition-colors">
          <Minus size={14} />
        </button>
        <div className="flex items-center gap-1 min-w-[40px] justify-center">
          <Type size={14} />
          <span className="font-black text-[10px]">{fontSize}</span>
        </div>
        <button onClick={() => changeFontSize(1)} className="hover:text-orange-400 transition-colors">
          <Plus size={14} />
        </button>
      </div>
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('siteid');
    navigate('/login');
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
  
    const checkAuth = () => {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        handleForceLogout();
        return;
      }
    
      api.get('/auth/check-session')
        .then(res => {
          if (res.data.access_token) {
            localStorage.setItem('token', res.data.access_token);
            console.log("Session extended automatically");
          }
        })
        .catch(err => {
          if (err.response && err.response.status === 401) {
            console.warn("Session expired. Redirecting...");
            handleForceLogout();
          }
        });
    };
  
    const handleForceLogout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('siteid'); 
      navigate('/login');
    };
  
    if (token) {
      checkAuth();
    } else if (location.pathname !== '/login') {
      handleForceLogout();
    }
  
    const authInterval = setInterval(() => {
      if (location.pathname !== '/login') {
        checkAuth();
      }
    }, 300000); 
  
    return () => clearInterval(authInterval);
  
  }, [navigate, location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setIsMenuOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setIsUserMenuOpen(false);
      if (notifMenuRef.current && !notifMenuRef.current.contains(event.target)) setIsNotifMenuOpen(false);
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) setIsLangMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navigationModulesConfig = useMemo(() => [
  {
      group: t('menu.assets'), 
      icon: <HardDrive size={16} />,
      items: [
        { label: t('menu.asset_registry'), path: "/assets", perm: ['assets', 'read'] },
        { label: t('menu.locations'), path: "/locations", perm: ['locations', 'read'] },
        { label: t('menu.meters'), path: "/meters", perm: ['meters', 'read'] },
        { label: t('menu.categories'), path: "/asset-categories", perm: ['asset-categories', 'read'] },
        { label: t('menu.failure_codes'), path: "/failure-codes", perm: ['failure-codes', 'read'] },
      ]
    },
    {
      group: t('menu.work_management'),
      icon: <Wrench size={16} />,
      items: [
        { label: t('menu.wo_tracking'), path: "/workorders", perm: ['workorders', 'read'] },
        { label: t('menu.incidents'), path: "/incidents", perm: ['incidents', 'read'] },
        { label: t('menu.service_requests'), path: "/sr", perm: ['sr', 'read'] },
      ]
    },
    {
      group: t('menu.inventory'),
      icon: <ShoppingCart size={16} />,
      items: [
        { label: t('menu.item_master'), path: "/items", perm: ['items', 'read'] },
        { label: t('menu.tools'), path: "/tools", perm: ['tools', 'read'] },
        { label: t('menu.balances'), path: "/inventory", perm: ['inventory', 'read'] },
        { label: t('menu.transfers'), path: "/inventoryusage", perm: ['inventoryusage', 'read'] },
        { label: t('apps.MR'), path: "/mr", perm: ['mr', 'read'] },        
        { label: t('menu.storerooms'), path: "/storerooms", perm: ['storerooms', 'read'] },               
      ]
    },    
    {
      group: t('menu.procurement'),
      icon: <ShoppingCart size={16} />,
      items: [
        { label: t('menu.po'), path: "/po", perm: ['po', 'read'] },
        { label: t('menu.pr'), path: "/pr", perm: ['pr', 'read'] },
        { label: t('menu.rfq'), path: "/rfq", perm: ['rfq', 'read'] },
        { label: t('menu.receiving'), path: "/receiving", perm: ['receiving', 'read'] },
        { label: t('menu.vendors'), path: "/vendors", perm: ['vendors', 'read'] },         
      ]
    },
    {
      group: t('menu.planning'),
      icon: <BarChart size={16} />,
      items: [
        { label: t('menu.pm_schedules'), path: "/pm", perm: ['pm', 'read'] },
        { label: t('menu.job_plans'), path: "/jp", perm: ['jp', 'read'] },
        { label: t('menu.safety_plans'), path: "/safety", perm: ['safety', 'read'] },
      ]
    },
    {
      group: t('menu.security'),
      icon: <ShieldAlert size={16} />,
      items: [
        { label: t('menu.user_list'), path: "/users", perm: ['users', 'read'] },
        { label: t('menu.groups'), path: "/roles", perm: ['roles', 'read'] },
      ]
    },
    {
      group: t('menu.settings'),
      icon: <Settings size={16} />,
      items: [
        { label: t('menu.field_config'), path: "/admin/field-config", perm: ['admin', 'write'] },
        { label: "Backup Manager", path: "/admin/backups", perm: ['admin', 'read'] },
      ]
    }
  ], [t]); 

  const filteredNavigation = useMemo(() => {
    return navigationModulesConfig.map(module => {
      const filteredItems = module.items.filter(item => hasPermission(item.perm[0], item.perm[1]));
      return filteredItems.length > 0 ? { ...module, items: filteredItems } : null;
    }).filter(module => module !== null);
  }, [navigationModulesConfig, hasPermission]);

  return (
    <header className="h-10 bg-[#f4f4f4] border-b border-gray-300 flex items-center justify-between px-4 shadow-sm z-[2000] relative select-none">
      
      {/* 🎯 استدعاء مودال النظام */}
      <SystemInfoModal 
        isOpen={isSystemInfoOpen} 
        onClose={() => setIsSystemInfoOpen(false)} 
        t={t} 
        isRTL={i18n.language === 'ar' || i18n.language === 'ur'}
      />

      {isPersonalModalOpen && (
        <PersonalInformationModal 
          isOpen={isPersonalModalOpen}
          onClose={() => setIsPersonalModalOpen(false)} 
        />
      )}
      {isChangePasswordOpen && (
        <ChangePasswordModal
          isOpen={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
        />
      )}

      {/* القسم الأيسر: أيقونة المنيو وشعار MAXEAM */}
      <div className="flex items-center gap-3">
        <button 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className={`p-1.5 rounded transition-all flex items-center gap-2 group ${isMenuOpen ? 'bg-[#005495] text-white shadow-inner' : 'text-[#005495] hover:bg-blue-100'}`}
        >
          {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        <div className="h-6 w-[1px] bg-gray-300 mx-2"></div>
        
        <div 
          className="flex items-center cursor-pointer group" 
          onClick={() => { navigate('/'); setIsMenuOpen(false); }}
        >
          <span><img src="/assets/teknova.png" alt="MAXEAM Logo" className="h-5 w-auto object-contain" /></span>
          <span style={{ marginLeft: '0.5rem' }} className="text-[9px] text-[#005495] font-bold uppercase tracking-[0.2em] hidden md:block opacity-70 group-hover:opacity-100 transition-opacity">Asset Management</span>
        </div>
      </div>

      {/* Mega Menu */}
      {isMenuOpen && (
        <div 
          ref={menuRef}
          className={`absolute top-10 ${i18n.language === 'ar' || i18n.language === 'ur' ? 'right-0' : 'left-0'} w-full md:w-[850px] bg-white shadow-[0_20px_50px_rgba(0,0,0,0.2)] border-x border-b border-gray-200 animate-in slide-in-from-top duration-200 z-[9999] rounded-b-md overflow-hidden text-start`}
        >
          {/* Header المنيو مع البحث */}
          <div className="grid grid-cols-1 md:grid-cols-3 bg-[#f8f9fa] border-b border-gray-100">
             <div className="p-3 flex items-center gap-3 border-e border-gray-200">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-[#005495]"><Settings size={18}/></div>
                <div className="flex flex-col">
                   <span className="text-[11px] font-black text-[#005495] uppercase">Start Center</span>
                   <span className="text-[9px] text-gray-400">Manage your workspace</span>
                </div>
             </div>
             <div className="col-span-2 p-2 px-4 flex items-center">
                <div className="relative w-full">
                   <Search size={14} className="absolute inset-y-0 start-3 my-auto text-gray-400" />
                   <input 
                      type="text" 
                      placeholder={t('search.placeholder')} 
                      className="w-full bg-white border border-gray-300 rounded-full py-2 ps-10 pe-4 text-[11px] focus:border-[#005495] outline-none transition-all shadow-sm"
                   />
                </div>
             </div>
          </div>

          {/* محتوى الموديولات (Grid) */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 p-2 max-h-[70vh] overflow-y-auto">
            {filteredNavigation.map((module, idx) => (
              <div key={idx} className="p-3 hover:bg-gray-50/80 transition-all rounded-sm">
                <div className="flex items-center gap-2 text-[#005495] font-black uppercase text-[10px] mb-3 border-b-2 border-[#005495]/10 pb-1.5">
                  {module.icon}
                  {module.group}
                </div>
                <div className="flex flex-col gap-0.5">
                  {module.items.map((item, i) => (
                    <div 
                      key={i}
                      onClick={() => { navigate(item.path); setIsMenuOpen(false); }}
                      className="flex items-center justify-between group cursor-pointer p-1.5 hover:bg-[#005495] hover:text-white rounded-sm transition-all text-[11px] text-gray-600 font-bold"
                    >
                      <span className="tracking-tight">{item.label}</span>
                      <ChevronRight size={10} className={`opacity-0 group-hover:opacity-100 transition-all transform ${i18n.language === 'ar' ? 'rotate-180 -translate-x-1' : 'translate-x-1'}`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="bg-[#1a3353] p-2 px-6 flex justify-between items-center text-[9px] text-white/70 font-bold uppercase tracking-widest">
            <span>{t('brand.version')}</span>
          </div>
        </div>
      )}

      {/* القسم الأيمن */}
      <div className="flex items-center gap-3 h-full">
        <FontControls /> 
        <div className="flex items-center gap-4 text-[#005495] border-l border-gray-300 pl-4 h-full">
          
          {/* --- Language Selector Dropdown --- */}
          <div className="relative flex items-center h-full" ref={langMenuRef}>
            <button 
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded transition-all ${isLangMenuOpen ? 'bg-blue-100' : 'hover:bg-blue-50'}`}
            >
              <Globe size={16} className="text-[#005495]" />
              <span className="text-[10px] font-black uppercase text-[#005495]">{i18n.language}</span>
              <ChevronDown size={10} className={`text-gray-400 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {isLangMenuOpen && (
              <div className="absolute top-10 right-0 w-32 bg-white shadow-xl border border-gray-200 rounded-md overflow-hidden animate-in fade-in slide-in-from-top-2 z-[5000]">
                <div className="flex flex-col py-1">
                  {[
                      { code: 'en', name: 'English', flag: '🇺🇸', dir: 'ltr' },
                      { code: 'ar', name: 'العربية', flag: '🇪🇬', dir: 'rtl' },
                      { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
                      { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' }, 
                      { code: 'it', name: 'Italiano', flag: '🇮🇹', dir: 'ltr' }, 
                      { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },  
                      { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },   
                      { code: 'ja', name: '日本語', flag: '🇯🇵', dir: 'ltr' },   
                      { code: 'ko', name: '한국어', flag: '🇰🇷', dir: 'ltr' },   
                      { code: 'ru', name: 'Русский', flag: '🇷🇺', dir: 'ltr' }, 
                      { code: 'ur', name: 'اُردُو', flag: '🇵🇰', dir: 'rtl' }   
                  ].map((lang) => (
                    <button
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          document.documentElement.dir = lang.dir;
                          document.body.dir = lang.dir;
                          localStorage.setItem('i18nextLng', lang.code);
                          localStorage.setItem('layoutDir', lang.dir);
                          setIsLangMenuOpen(false);
                          
                          if (lang.dir === 'rtl') {
                            document.body.style.fontFamily = "'Cairo', sans-serif";
                          } else {
                            document.body.style.fontFamily = "'Inter', sans-serif";
                          }
                        }}
                        className={`flex items-center gap-3 px-4 py-2 text-[11px] font-bold transition-all hover:bg-[#005495] hover:text-white
                          ${i18n.language === lang.code ? 'bg-blue-50 text-[#005495]' : 'text-gray-600'}`}
                      >
                      <span className="text-sm">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* 🔔 قسم الجرس (Notifications) */}
          <div className="relative flex items-center h-full" ref={notifMenuRef}>
            <button 
              onClick={() => setIsNotifMenuOpen(!isNotifMenuOpen)}
              className="relative p-1 text-[#005495] hover:bg-blue-100 rounded-full transition-colors"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white border-2 border-[#f4f4f4]">
                  {unreadCount}
                </span>
              )}
            </button>

              {isNotifMenuOpen && (
                <div className={`absolute top-10 ${i18n.language === 'ar' || i18n.language === 'ur' ? 'left-0' : 'right-0'} w-80 bg-white shadow-2xl border border-gray-200 rounded-md overflow-hidden animate-in fade-in slide-in-from-top-2 z-[5000]`}>
                  <div className="p-3 border-b bg-gray-50 flex justify-between items-center">
                    <span className="text-[10px] font-black text-[#005495] uppercase tracking-widest">{t('notifications.title')}</span>
                    {unreadCount > 0 && (
                      <span className="text-[9px] bg-blue-100 text-[#005495] px-2 py-0.5 rounded-full font-bold">
                        {unreadCount} {t('notifications.new_alerts')}
                      </span>
                    )}
                  </div>

                  <div className="max-h-[400px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center flex flex-col items-center gap-2 opacity-40">
                        <Bell size={24} className="text-gray-400" />
                        <span className="text-[10px] font-bold text-gray-500 uppercase">{t('notifications.empty')}</span>
                      </div>
                    ) : (
                      notifications.map((notif, idx) => (
                        <div 
                          key={idx}
                          onClick={() => handleNotifClick(notif)}
                          className="p-3 border-b border-gray-50 hover:bg-blue-50 cursor-pointer transition-all group relative"
                        >
                          <div className={`flex flex-col gap-1 ${i18n.language === 'ar' || i18n.language === 'ur' ? 'text-right' : 'text-left'}`}>
                            <div className="flex justify-between items-start">
                              <span className="text-[11px] font-black text-gray-700 group-hover:text-[#005495]">{notif.title}</span>
                              <span className="text-[8px] text-gray-400 font-bold uppercase">{notif.app_name}</span>
                            </div>
                            <p className="text-[10px] text-gray-500 leading-tight font-medium">{notif.message}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[9px] font-black text-[#005495] uppercase tracking-tighter">Ref: {notif.ref_id}</span>
                              <ChevronRight size={10} className={`text-[#005495] opacity-0 group-hover:opacity-100 transition-all transform ${i18n.language === 'ar' || i18n.language === 'ur' ? 'rotate-180 -translate-x-1' : 'translate-x-1'}`} />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <div className="p-2 border-t bg-gray-50 text-center">
                    <button className="text-[9px] font-black text-[#005495] uppercase hover:underline">{t('notifications.view_all')}</button>
                  </div>
                </div>
              )}
            </div>

          {/* 👤 قسم ملف المستخدم (User Profile) */}
          <div className="relative h-full flex items-center" ref={userMenuRef}>
            <div 
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className={`flex items-center gap-2 cursor-pointer border-gray-300 hover:opacity-80 transition-opacity h-full ${i18n.language === 'ar' || i18n.language === 'ur' ? 'border-l pl-4' : 'border-r pr-4'}`}
            >
              <div className="w-6 h-6 bg-[#1a3353] rounded-sm flex items-center justify-center text-white text-[10px] font-black shadow-md uppercase">
                {userData.initial}
              </div>
              <div className={`flex flex-col leading-none hidden sm:flex ${i18n.language === 'ar' || i18n.language === 'ur' ? 'text-right' : 'text-left'}`}>
                <span className="text-[10px] font-black text-[#005495] uppercase">{userData.name}</span>
                <span className="text-[8px] text-gray-400 font-bold uppercase">{userData.role}</span>
              </div>
              <ChevronDown size={12} className={`text-gray-400 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
            </div>

            {/* القائمة المنسدلة لليوزر */}
            {isUserMenuOpen && (
              <div className={`absolute top-10 ${i18n.language === 'ar' || i18n.language === 'ur' ? 'left-0' : 'right-0'} w-48 bg-white shadow-xl border border-gray-200 rounded-b-md overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150 z-[3000]`}>
                <div className={`p-2 border-b bg-gray-50 ${i18n.language === 'ar' || i18n.language === 'ur' ? 'text-right' : 'text-left'}`}>
                  <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-2">{t('user_settings.title')}</span>
                </div>
                
                <div className="flex flex-col">
                  <button 
                    onClick={() => { setIsPersonalModalOpen(true); setIsUserMenuOpen(false); }}
                    className={`flex items-center gap-3 p-2.5 px-4 text-[11px] font-bold text-gray-600 hover:bg-[#005495] hover:text-white transition-all group ${i18n.language === 'ar' || i18n.language === 'ur' ? 'flex-row-reverse text-right' : 'text-left'}`}
                  >
                    <Info size={14} className="shrink-0" />
                    <span className="flex-1">{t('user_settings.default_info')}</span>
                  </button>
                  {/*}
                  <button 
                    onClick={() => { navigate('/profile/personal'); setIsUserMenuOpen(false); }}
                    className={`flex items-center gap-3 p-2.5 px-4 text-[11px] font-bold text-gray-600 hover:bg-[#005495] hover:text-white transition-all group ${i18n.language === 'ar' || i18n.language === 'ur' ? 'flex-row-reverse text-right' : 'text-left'}`}
                  >
                    <User size={14} className="shrink-0" />
                    <span className="flex-1">{t('user_settings.personal_info')}</span>
                  </button>
                  */}
                  <button 
                    onClick={() => { setIsChangePasswordOpen(true); setIsUserMenuOpen(false); }}
                    className={`flex items-center gap-3 p-2.5 px-4 text-[11px] font-bold text-gray-600 hover:bg-[#005495] hover:text-white transition-all group ${i18n.language === 'ar' || i18n.language === 'ur' ? 'flex-row-reverse text-right' : 'text-left'}`}
                  >
                    <Key size={14} className="shrink-0" />
                    <span className="flex-1">{t('user_settings.password_info')}</span>
                  </button>

                  <div className="h-[1px] bg-gray-100 my-1"></div>

                  {/* 🎯 زرار عن النظام (System Info) */}
                  <button 
                    onClick={() => { setIsSystemInfoOpen(true); setIsUserMenuOpen(false); }}
                    className={`flex items-center gap-3 p-2.5 px-4 text-[11px] font-bold text-gray-600 hover:bg-[#005495] hover:text-white transition-all group ${i18n.language === 'ar' || i18n.language === 'ur' ? 'flex-row-reverse text-right' : 'text-left'}`}
                  >
                    <Cpu size={14} className="shrink-0" />
                    <span className="flex-1">{t('user_settings.system_info', 'عن النظام')}</span>
                  </button>

                  <div className="h-[1px] bg-gray-100 my-1"></div>

                  <button 
                    onClick={handleLogout}
                    className={`flex items-center gap-3 p-2.5 px-4 text-[11px] font-bold text-red-600 hover:bg-red-50 transition-all ${i18n.language === 'ar' || i18n.language === 'ur' ? 'flex-row-reverse text-right' : 'text-left'}`}
                  >
                    <LogOut size={14} className="shrink-0" />
                    <span className="flex-1">{t('user_settings.sign_out')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          
        </div>
      </div>
    </header>
  );
};

export default Header;